import * as Y from 'yjs';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { syncedStore } from '@syncedstore/core';
import { useSyncedStore } from '@syncedstore/react';
import {
  InitViaState,
  setYDocConfigMeta,
  shouldSyncFromBlox,
} from 'components/RichTextEditor/hooks/useCreateEditor';
import {
  FullEditorialAsset,
  RelatedContentItem,
  RelatedAsset,
} from 'utility/types/dataProvider';
import { isPlainObject } from 'remeda';
import {
  CollabProviderState,
  SyncedInitialYDocConfigState,
} from '../AssetConfigUtilities';

/**
 * IMPORTANT - README
 *
 * Documentation:
 * https://docs.yjs.dev/getting-started/working-with-shared-types#caveats
 * https://docs.yjs.dev/api/faq
 * https://docs.yjs.dev/api/y.doc
 * https://syncedstore.org/docs/react/
 * https://syncedstore.org/docs/advanced/yjs
 * https://syncedstore.org/docs/api/
 * --------------------------------------------------
 * Synced store for various fields on ydoc.
 * The types here will be important to know what
 * potentially exists on the ydoc store.
 *
 * Generally it will be a good idea to make all
 * properties on the store optional and use optional
 * chaining all the way down when reading data.
 *
 * When writing to the store its also important
 * to wrap the entire write in a try/catch block
 * as well as running the data about to be set
 * through a cleaner func to ensure that the data
 * is safe to write, and do all of this inside of
 * one ydoc.transact block if multiple things are
 * being updated at once.
 * --------------------------------------------------
 * Because we are now writing to the ydoc manually it
 * is effectively a database in collab mode, and so
 * if accidentally 'corrupted' the data it can crash the view,
 * which may get synced to all other users and potentially
 * crash all of their views too... Unfortunatley may not
 * be easy to recover from this easily once its synced
 * across different users documents or to cloud, until
 * we have the ability to delete ydocs from the cloud.
 * --------------------------------------------------
 * PITFALLS / WARNINGS:
 * >> Undefineds, inside an array, will crash SyncedStores!!!
 *
 * This applies to both object and array types.
 * eg: { objKey:[undefined] }, myArr:[undefined], both bad!
 *
 * The easiest way is handle this to use the
 * cleanObjForYjs function to sanitize the data
 * before attempting to write it to the ydoc store.
 *
 * Effectively these stores should only contain stuff that
 * you can serialize to JSON, so no functions, symbols etc.
 *
 * https://github.com/YousefED/SyncedStore/issues/136
 * https://github.com/YousefED/SyncedStore/issues/108
 * --------------------------------------------------
 *
 * YJS Info:
 * Consider using a single top-level YMap store.
 * Top-level shared types (stores) CANNOT be deleted, so
 * so you may want to structure all data in a
 * single top-level YMap. - eg, once you defined a
 * key (a store), and a type of store, you may
 * not change the name of the key or type.
 * https://docs.yjs.dev/api/faq#:~:text=Consider%20using%20a%20single%20top%2Dlevel%20YMap
 *
 * --------------------------------------------------
 * It can be difficult to debug issues with Yjs
 * and SyncedStore in logs, so consider using this:
 *
 * import { ObjectInspector } from 'react-inspector';
 *
 * <ObjectInspector data={{ state: syncState.syncStoreData }} />
 *
 * If trying to console log and you see Proxy(objects) then
 * you can json stringify or "copy" in the devtools
 * and paste to see the actual data....
 *
 */

/**
 * Sync Store Data Types
 * See FullEditorialAsset type for references
 */

type PreviewSizes = {
  url?: string;
  width?: number;
  height?: number;
};

export type PreviewsType = {
  size1?: PreviewSizes;
  size2?: PreviewSizes;
  display?: PreviewSizes;
  thumbnail?: PreviewSizes;
};

// see: FileViewCrop / ImageData.ts
type CropObj = { left?: number; top?: number; width?: number; height?: number };
export type CropType = CropObj | false | null;

export type FileMetaType = {
  type?: string;
  size?: number;
  url?: string;
  width?: number;
  height?: number;
  view_crop?: CropType;
  add_binary?: string | null;
  remove_binary?: boolean;
  previews?: PreviewsType;
};

// Asset properties
export type PropertiesStore = {
  abuse?: string | number | null;
  print_ready?: boolean | null;
  presentation?: string | null;
  article_type?: string | null;
  social_twitter_card?: string | null;
  country?: string | null;
  region?: string | null;
  cover_align_horizontal?: string | null;
  cover_align_vertical?: string | null;
  cover_text_color?: string | null;
  cover_text_background?: string | null;
  cover_effect?: string | null;
  no_publish?: number | null;
  no_reactions?: boolean | null;
  no_comments?: boolean | null;
  workflow_id?: string | null;
  workflow_process_id?: string | null;
  origin_domain?: string | null;
};

// --------------------------------------------------
// Sync Store Root Creation
// --------------------------------------------------

// Field visibility state type
export type FieldVisibilityState = {
  hammerHidden?: boolean;
  kickerHidden?: boolean;
  subheadlineHidden?: boolean;
};

// Shape of data in the root ykey syncStoreData.
type SyncStoreData = {
  // File slots also contain certain hidden fields
  // eg: add_binary, remove_binary, view_crop
  files?: {
    image?: FileMetaType;
    social?: FileMetaType;
    preview?: FileMetaType; // Teaser
    cover?: FileMetaType;
  };
  // Related assets
  children?: RelatedAsset[];
  siblings?: RelatedAsset[];
  parents?: RelatedAsset[];
  related_content?: RelatedContentItem[];
  // Draft
  draft?: boolean | null;
  // Asset properties (no_publish, workflow, etc)
  properties?: PropertiesStore;
  // Dates
  start_time?: string | null;
  archive_time?: string | null;
  delete_time?: string | null;
  update_time?: string | null;
  // Field visibility (for "removable" fields)
  fieldVisibility?: FieldVisibilityState;
};

// State that is communicated over ydoc, at this key top level key.
type SyncStoreRoot = {
  syncStoreData: SyncStoreData;
};

type SetupSyncStoreArgs = {
  yDoc?: Y.Doc;
};

type SetupSyncStoreRes = ReturnType<typeof syncedStore<SyncStoreRoot>>;

/**
 * Initializes (or gets existing) top level key 'syncStoreData' on ydoc.
 * Object must be empty for syncedstore init.
 */
function setupNxtSyncStore({ yDoc }: SetupSyncStoreArgs): SetupSyncStoreRes {
  const initObj: SyncStoreData = {};
  const store = syncedStore({ syncStoreData: initObj }, yDoc);
  return store;
}

// --------------------------------------------------
// Helper functions
// --------------------------------------------------

/**
 * Helper attempts JSON.stringify then parse on a JS Obj
 * to cleanup any undefineds in array, etc for yjs storage.
 * Can optionally pass throwOnError false to get
 * back an empty object if it fails, if desired.
 * See README at top of file for more information.
 */

type TCleanObjForYjsArgs<T extends object> = {
  value: T;
  throwOnError?: boolean;
};
type TCleanObjForYjsRes<T extends object> = Partial<T>;

export function cleanObjForYjs<T extends object>({
  value,
  throwOnError = true,
}: TCleanObjForYjsArgs<T>): TCleanObjForYjsRes<T> {
  try {
    if (!value || !isPlainObject(value)) {
      throw new Error('Argument was not an plain object');
    }
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#description
    const cleaned = JSON.parse(JSON.stringify(value)) as Partial<T>;
    // In some edge cases json stringify will convert empty objs
    // to undefined or null, we always want a plain object back..
    if (!cleaned || !isPlainObject(cleaned)) {
      throw new Error('Cleaned was not a plain object');
    }
    // Cleaned should have converted any undefs in arrays to null, etc
    // and should be safe to now set into the yjs map or synced store.
    return cleaned;
  } catch (err) {
    console.warn('[cleanObjForYjs]:', err);
    // Throw if specified, wont return anything.
    // Else return empty object if requested...
    if (throwOnError) {
      throw err;
    }
    return {};
  }
}

/**
 * Similar to cleanObjForYjs -- but instead this is closer to what
 * just stringify and then parse would return, however if it errors
 * eg (JSON.parse(JSON.stringify(undefined))) - can return undefined
 * rather than throwing an error, which may be needed in some areas,
 * where you may not trust what the result of reading a proxy obj
 * directly is, or if you want to avoid throwing an error if parse fails.
 * In almost all of cases likely want to use cleanObjForYjs instead
 * before writing anything into the yjs doc store...
 */

export type TSimpleSafeParseRes<T> = Partial<T> | undefined;

export function simpleSafeParse<T>(
  value: T,
  throwOnError: boolean = false,
): TSimpleSafeParseRes<T> {
  try {
    // ok here to return null, [], {}, etc, this just ensures that
    // if json parse throws, we can get undefined back instead of throw
    const cleaned = JSON.parse(JSON.stringify(value)) as Partial<T>;
    return cleaned;
  } catch (err) {
    console.warn('[simpleSafeParse]:', err);
    // Throw if specified, wont return anything.
    // Else return an undefined if requested...
    if (throwOnError) {
      throw err;
    }
    return undefined;
  }
}

/**
 * Helper function to format a FullEditorialAsset
 * record to SyncStoreData object, used for
 * helping initial syncs and setting up the store.
 *
 * Also if any other initial shared data is needed
 * needed, we can set them up here as well?
 *
 * These generally should match the fallbacks
 * as defined in AssetConfigUtilities.tsx, at
 * some point we need to unify both sides together.
 *
 * Note: If children, siblings, parents, related_content
 * or generally, these store values are not the same as
 * the form default values, you may get a false positive
 * dirty state on the form due to <RelationshipSyncBridge>
 * and other sync effects, so be careful to ensure they match.
 * The ydoc is always available even not in collaboration mode.
 */

type FmtBloxToSyncStoreDataArgs = {
  bloxDoc?: FullEditorialAsset;
  throwErr?: boolean; // default true
};
export function fmtBloxToSyncStoreData({
  bloxDoc,
  throwErr = true,
}: FmtBloxToSyncStoreDataArgs): SyncStoreData {
  try {
    const imageData = bloxDoc?.files?.image ?? {};
    const socialData = bloxDoc?.files?.social ?? {};
    const previewData = bloxDoc?.files?.preview ?? {};
    const coverData = bloxDoc?.files?.cover ?? {};
    const propertiesData: PropertiesStore = {
      abuse: bloxDoc?.properties?.abuse,
      print_ready: bloxDoc?.properties?.print_ready ?? false,
      presentation: bloxDoc?.properties?.presentation ?? null,
      article_type: bloxDoc?.properties?.article_type ?? null,
      social_twitter_card: bloxDoc?.properties?.social_twitter_card ?? '',
      country: bloxDoc?.properties?.country ?? null,
      region: bloxDoc?.properties?.region ?? null,
      cover_align_horizontal: bloxDoc?.properties?.cover_align_horizontal ?? null,
      cover_align_vertical: bloxDoc?.properties?.cover_align_vertical ?? null,
      cover_text_color: bloxDoc?.properties?.cover_text_color ?? null,
      cover_text_background: bloxDoc?.properties?.cover_text_background ?? null,
      cover_effect: bloxDoc?.properties?.cover_effect ?? null,
      no_publish: bloxDoc?.properties?.no_publish ?? null,
      no_reactions: bloxDoc?.properties?.no_reactions ?? false,
      no_comments: bloxDoc?.properties?.no_comments ?? false,
      workflow_id: bloxDoc?.properties?.workflow_id ?? null,
      workflow_process_id: bloxDoc?.properties?.workflow_process_id ?? null,
      origin_domain: bloxDoc?.properties?.origin_domain ?? null,
    };

    const draft = bloxDoc?.draft ?? true;
    const start_time = bloxDoc?.start_time ?? null;
    const archive_time = bloxDoc?.archive_time ?? null;
    const delete_time = bloxDoc?.delete_time ?? null;
    const update_time = bloxDoc?.update_time ?? null;

    const children = mapRelationsToStore(bloxDoc?.children);
    const siblings = mapRelationsToStore(bloxDoc?.siblings);
    const parents = mapRelationsToStore(bloxDoc?.parents);
    const related_content = mapRelatedContentToStore(bloxDoc?.related_content);

    // Setup formatted sync store data
    const fmtStoreData: SyncStoreData = {
      files: {
        image: imageData,
        social: socialData,
        preview: previewData,
        cover: coverData,
      },
      children: children,
      siblings: siblings,
      parents: parents,
      related_content: related_content,
      properties: propertiesData,
      draft: draft,
      start_time: start_time,
      archive_time: archive_time,
      delete_time: delete_time,
      update_time: update_time,
      // Field visibility defaults (hidden)
      fieldVisibility: {
        hammerHidden: true,
        kickerHidden: true,
        subheadlineHidden: true,
      },
    };

    // Run through cleaning func to ensure before returning for yjs...
    const cleanData = cleanObjForYjs({
      value: fmtStoreData,
      throwOnError: throwErr,
    });

    return cleanData;
  } catch (err) {
    // If errored log and optionally throw upstream
    // or return empty object if not throwing
    console.error('[fmtBloxToSyncStoreData]:', err);
    if (throwErr) {
      throw err;
    }
    return {};
  }
}

/**
 * Maps a list of related assets to a format suitable for the store.
 * @param list - The list of related asset items to map.
 * @returns An array of mapped RelatedAsset objects.
 */

type RelatedListArg = RelatedAsset[] | undefined;
function mapRelationsToStore(list: RelatedListArg): RelatedAsset[] {
  if (!Array.isArray(list)) {
    return [];
  }

  const mapped = list.map((item) => {
    const result = {
      uuid: item?.uuid,
      app: item?.app,
      type: item?.type,
      title: item?.title,
      start_time: item?.start_time,
      archive_time: item?.archive_time,
      status: item?.status,
      draft: item?.draft,
      no_publish: item?.no_publish,
      previews: item?.previews,
      authors: item?.authors,
      byline: item?.byline,
      summary: item?.summary,
    };
    return result;
  });

  // JSON serialization removes undefined properties while
  // preserving null values, important for yjs storage.
  const cleaned = JSON.parse(JSON.stringify(mapped));
  return cleaned;
}

/**
 * Maps a list of related content items to a format suitable for the store.
 * @param list - The list of related content items to map.
 * @returns An array of mapped RelatedContentItem objects.
 */
type RelContentListArg = RelatedContentItem[] | undefined;
function mapRelatedContentToStore(list: RelContentListArg): RelatedContentItem[] {
  if (!Array.isArray(list)) {
    return [];
  }

  const mapped = list.map((item) => {
    const result = {
      id: item?.id,
      inline: item?.inline,
      style: item?.style,
      type: item?.type,
      title: item?.title,
      content: item?.content,
      environment: item?.environment,
      style_properties: item?.style_properties,
    };
    return result;
  });

  // JSON serialization removes undefined properties while
  // preserving null values, important for yjs storage.
  const cleaned = JSON.parse(JSON.stringify(mapped));
  return cleaned;
}

// --------------------------------------------------
// UseNxtSyncStore Hook
// --------------------------------------------------

type UseNxtSyncStoreArgs = {
  yDoc?: Y.Doc;
  bloxDoc?: FullEditorialAsset;
  syncedInitialYDocConfig?: SyncedInitialYDocConfigState;
  collabProvider?: CollabProviderState;
  isReadOnlyMode?: boolean;
};

// store and state are: MappedTypeDescription<SyncStoreRoot>
// but we cant import MappedTypeDescription, its not exported...
// So instead well have to use the ReturnType of the getter...
export type UseNxtSyncStoreRtn = {
  store: SetupSyncStoreRes; // raw store if needed
  state: SetupSyncStoreRes; // reactive state via hook
  initVia: InitViaState; // null or where was init from
  updateFieldVisibility: (args: {
    field: keyof FieldVisibilityState;
    hidden: boolean;
  }) => void;
};

export function useNxtSyncStore({
  yDoc,
  bloxDoc,
  collabProvider,
  syncedInitialYDocConfig,
  isReadOnlyMode,
}: UseNxtSyncStoreArgs): UseNxtSyncStoreRtn {
  const [initVia, setInitVia] = useState<InitViaState>(null);
  const store = useMemo(() => setupNxtSyncStore({ yDoc: yDoc }), [yDoc]);
  const state = useSyncedStore(store);

  // collab provider exists and initial ydoc config state not null
  const collabModeAndSynced = useMemo(() => {
    return Boolean(collabProvider && syncedInitialYDocConfig);
  }, [collabProvider, syncedInitialYDocConfig]);

  /**
   * Helper to write formatted api record
   * data to syncstore in one transaction
   */
  type WriteSyncStoreDataArgs = {
    yDoc?: Y.Doc;
    record?: FullEditorialAsset;
    updateConfigMeta?: boolean;
    resetFieldVisibilityFromRecord?: boolean; // Flag to reset field visibility based on record content
  };

  const writeSyncStoreData = useCallback(
    ({
      yDoc,
      record,
      updateConfigMeta,
      resetFieldVisibilityFromRecord,
    }: WriteSyncStoreDataArgs) => {
      try {
        // formatted data from blox doc
        const newStoreData = fmtBloxToSyncStoreData({ bloxDoc: record });
        const newFilesData = newStoreData?.files ?? {};
        const newPropsData = newStoreData?.properties ?? {};

        yDoc?.transact(() => {
          // Capture existing fieldVisibility BEFORE resetting store
          // This preserves collaborative state when updating other fields
          const existingFieldVisibility = state.syncStoreData.fieldVisibility
            ? {
                hammerHidden: state.syncStoreData.fieldVisibility.hammerHidden,
                kickerHidden: state.syncStoreData.fieldVisibility.kickerHidden,
                subheadlineHidden: state.syncStoreData.fieldVisibility.subheadlineHidden,
              }
            : undefined;

          // reset anything that may currently exist
          // in the store for files, properties, etc...
          state.syncStoreData.files = {};
          state.syncStoreData.properties = {};
          // Set data to store for files and properties
          Object.assign(state.syncStoreData.files, newFilesData);
          Object.assign(state.syncStoreData.properties, newPropsData);
          // Dates
          state.syncStoreData.start_time = newStoreData?.start_time ?? null;
          state.syncStoreData.archive_time = newStoreData?.archive_time ?? null;
          state.syncStoreData.delete_time = newStoreData?.delete_time ?? null;
          state.syncStoreData.update_time = newStoreData?.update_time ?? null;
          // Draft
          state.syncStoreData.draft = newStoreData?.draft ?? true;
          // Relationships
          state.syncStoreData.children = newStoreData?.children ?? [];
          state.syncStoreData.siblings = newStoreData?.siblings ?? [];
          state.syncStoreData.parents = newStoreData?.parents ?? [];
          state.syncStoreData.related_content = newStoreData?.related_content ?? [];

          // Field visibility handling
          // This is UI state (not record data) that controls show/hide of fields in collaboration
          if (resetFieldVisibilityFromRecord && record) {
            // Bloxsync Record is the source of truth
            // Hide fields that don't have content, regardless of previous collaborative state
            // This ensures that if content was removed in non-collab mode, fields are hidden
            state.syncStoreData.fieldVisibility = {
              hammerHidden: !record?.properties?.hammer,
              kickerHidden: !record?.properties?.kicker,
              subheadlineHidden: !record?.properties?.subheadline,
            };
          } else if (existingFieldVisibility) {
            // Collaborative state exists - preserve it
            // A user has revealed a field to display for the collab session, even if record has no content
            state.syncStoreData.fieldVisibility = existingFieldVisibility;
          } else {
            // No record-based init requested and no existing state - use defaults
            state.syncStoreData.fieldVisibility = newStoreData?.fieldVisibility ?? {
              hammerHidden: true,
              kickerHidden: true,
              subheadlineHidden: true,
            };
          }

          // update the ydoc config meta (ydoc_revision_id)
          // with the records information if requested...
          if (updateConfigMeta) {
            setYDocConfigMeta({
              yDoc: yDoc,
              bloxDoc: record,
            });
          }
        });
      } catch (error) {
        console.error('[writeSyncStore]:', error);
        throw error;
      }
    },
    [state.syncStoreData],
  );

  /**
   * Helper to update field visibility state in sync store
   */
  type UpdateFieldVisibilityArgs = {
    field: keyof FieldVisibilityState;
    hidden: boolean;
  };

  const updateFieldVisibility = useCallback(
    ({ field, hidden }: UpdateFieldVisibilityArgs) => {
      try {
        yDoc?.transact(() => {
          // Initialize fieldVisibility if it doesn't exist
          if (!state.syncStoreData.fieldVisibility) {
            state.syncStoreData.fieldVisibility = {
              hammerHidden: true,
              kickerHidden: true,
              subheadlineHidden: true,
            };
          }
          // Update the specific field
          state.syncStoreData.fieldVisibility[field] = hidden;
        });
      } catch (error) {
        console.error('[updateFieldVisibility]:', error);
        throw error;
      }
    },
    [yDoc, state.syncStoreData],
  );

  /**
   * We may want to test removing the initVia guard here,
   * as if we remove the guard, then anytime the recordCopy
   * changes (eg: on durable saves to blox), this effect can
   * trigger a re-run, so if the API record had some updated
   * data after a save (eg some auto-set api behaviour) that
   * update would be picked up here and distributed to users.
   *
   * Briefly tested this and everything seemed to worked fine.
   * Even if a user started off in cloud mode, then saved, the revision id
   * on recordCopy would increase, and it would re-run the setBloxContent
   * if/else branch, which would effectively 'auto-update syncstore on saves'
   * which should propagate to all connected users when any user saves...
   *
   * Note: we have started doing some of this explicitly in the
   * RightSide / onSave handler anyway, so if removing the guard
   * we will need to adjust the areas logic accordingly.
   */

  // Effect will be very similar to the stuff that useCreateEditor
  // does in its onCreate handler, to determine what data to sync...
  useEffect(() => {
    // need a ydoc to proceed
    if (!yDoc) {
      return;
    }
    // skip if initVia is set, but see comment
    // above as this may change in future...
    if (initVia) {
      return;
    }

    // no collab provider - we can set store data when ready
    if (!collabProvider) {
      try {
        // write the formatted data to the store
        // can skip config meta update, not in collab
        writeSyncStoreData({
          yDoc: yDoc,
          record: bloxDoc,
          updateConfigMeta: false,
          resetFieldVisibilityFromRecord: true,
        });

        setInitVia('blox');
      } catch (error) {
        console.error('[useNxtSyncStore-1]:', error);
      }
    }

    // collab mode: provider exists and we have a copy of the
    // initial ydoc config for revision id comparisons to determine
    // set content from bloxdoc or skip and use collab cloud contents...
    if (collabModeAndSynced) {
      try {
        // determine flow by revision id comparison
        const setBloxContent = shouldSyncFromBlox({
          syncedInitialYDocConfig: syncedInitialYDocConfig,
          bloxDoc: bloxDoc,
        });
        // if blox considered newer than cloud contents
        // set/replace contents in the ydoc with blox data
        if (setBloxContent) {
          // write the formatted data to the store
          // and update the config meta on the ydoc
          // Reset field visibility based on record content (only if no collab state exists yet)
          writeSyncStoreData({
            yDoc: yDoc,
            record: bloxDoc,
            updateConfigMeta: true,
            resetFieldVisibilityFromRecord: true,
          });

          setInitVia('bloxsync');
        } else {
          // no sync - use the ydoc cloud content directly
          // the revision has not changed since last loaded
          setInitVia('cloud');
        }
      } catch (error) {
        console.error('[useNxtSyncStore-2]:', error);
      }
    }
  }, [
    bloxDoc,
    yDoc,
    collabProvider,
    syncedInitialYDocConfig,
    collabModeAndSynced,
    initVia,
    writeSyncStoreData,
  ]);

  // For the most part you will access the state:
  // Reads: const myValue = state?.syncStoreData?.whatever;
  // Write: can write direct, but prefer inside doc.transact()
  // when writing many fields at once it is better to do
  // everything in one  transaction as the yjs docs recommend
  // and always do writes inside of a trycatch block...
  // Can also read directly and bypass this hook via
  // const state = useSyncedStore(store.whatever.etc);
  // See: https://syncedstore.org/docs/react/

  const result: UseNxtSyncStoreRtn = {
    state: state,
    store: store,
    initVia: initVia,
    updateFieldVisibility: updateFieldVisibility,
  };

  return result;
}

/**
 * --------------------------------------------------
 * debug code testing... can be used to confirm
 * something is being written to the ydoc in one txn
 * 1: Ydoc transactions you  can add an origin string
 * to whatever, and listen for that in other areas.
 * eg: maybe useful to do a user uuid or something.
 * 2. txns have a boolean flag indicating if the event
 * was emitted by the local client or not, also useful.
 * --------------------------------------------------
 *
 *
 *  export function addTxLogger(doc?: Y.Doc, filterOrigin?: string) {
 *      const onBefore = (tr: Y.Transaction) => {
 *        if (!filterOrigin || tr.origin === filterOrigin) {
 *          console.log('[yDoc] tx:start', {
 *            origin: tr.origin,
 *            local: tr.local,
 *            t: performance.now(),
 *          });
 *        }
 *      };
 *      const onAfter = (tr: Y.Transaction) => {
 *        if (!filterOrigin || tr.origin === filterOrigin) {
 *          console.log('[yDoc] tx:end', {
 *            origin: tr.origin,
 *            local: tr.local,
 *            changedParents: tr.changedParentTypes.size,
 *            t: performance.now(),
 *          });
 *        }
 *      };
 *      doc?.on('beforeTransaction', onBefore);
 *      doc?.on('afterTransaction', onAfter);
 *      return () => {
 *        doc?.off('beforeTransaction', onBefore);
 *        doc?.off('afterTransaction', onAfter);
 *      };
 *
 *  const removeTxLogger = addTxLogger(yDoc, 'some-origin-set');
 *
 * // ensure guard mount or will log multiple times
 *  useEffect(() => {
 *    if (!yDoc) return;
 *    const detach = addTxLogger(yDoc, 'some-origin-set');
 *    return detach;
 *  }, [yDoc]
 *
 * --------------------------------------------------
 * It is possible to define multiple types on a single store,
 * but it may be best to let tiptap handle the rich text
 * Yjs/ CRDT text/richtext merge operations.
 * type RichRoot = {
 *   title: 'text'; // Y.Text
 *   rich: 'xml';   // Y.XmlFragment
 *   myobj: {};       // plain object (Y.Map)
 *   mylist: any[];   // array (Y.Array)
 * };
 * Create the store bound to a Y.Doc
 * function getMixedStore(yDoc: Y.Doc) {
 *   return syncedStore<RichRoot>(
 *     { title: 'text', rich: 'xml', myobj: {}, mylist: [] },
 *     yDoc
 *   );
 * }
 */
